"use client";

import classNames from "classnames";

import FontAwesome from "../../utilities/FontAwesome";
import { LanguageDropDown } from "./LanguageDropDown";
import styles from "../styles/LanguageTool.module.scss";
import { color_lightness } from "../../../utils/colorcontrast";
import { observer } from "mobx-react";
import { useStores } from "../../../stores/useStores";
import { useRef, useState } from "react";
import { Dialog } from "radix-ui";
import PoweredByGoogleTranslate from "../../../assets/images/greyscale-regular@2x.png";
import { ThemeProvider } from "../../radix/ThemeProvider";
import { TranslatedText } from "../../utilities/TranslatedText";

export const LanguageSelector = (props: {
  handleSelect?: () => void;
  className?: string;
}) => {
  const translationClassName = classNames({
    [styles.translationElement]: true,
    [props.className || ""]: props.className,
  });

  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  let title;
  if (props.handleSelect) {
    // handleSelect is passed down for SchoolBlocks but not CleverFeed, where LanguageSelector is rendered within a Dialog and we need Dialog.Title for accessibility
    title = (
      <Dialog.Title>
        <TranslatedText>Select your language</TranslatedText>
      </Dialog.Title>
    );
  } else {
    title = <TranslatedText as={"h2"}>Select your language</TranslatedText>;
  }

  return (
    <div className={translationClassName} ref={setContainer}>
      <div className={styles.inner}>
        <div className={styles.selectContainer}>
          <FontAwesome
            prefix={"fas"}
            name={"fa-language"}
            className={styles.icon}
            size={"2x"}
          />
          {title}
        </div>
        <div className={styles.learnMoreContainer}>
          <TranslatedText as={"div"}>
            Set your preferred language.&nbsp;
          </TranslatedText>
          <img
            width={150}
            alt={"Powered by Google Translate"}
            src={PoweredByGoogleTranslate}
          />
        </div>
        <div className={styles.dropdownContainer}>
          <TranslatedText as={"div"} className={styles.heading2}>
            Language
          </TranslatedText>
          {container && (
            <ThemeProvider theme={{ portalContainer: { current: container } }}>
              <LanguageDropDown handleSelect={props.handleSelect} />
            </ThemeProvider>
          )}
        </div>
      </div>
    </div>
  );
};

const LanguageTool = observer(() => {
  const { styleStore } = useStores();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const toggleOpen = () => setOpen((v) => !v);

  let backgroundColor = "white",
    color = "black";
  if (styleStore.themeID === "1") {
    const lightness = color_lightness(styleStore.cssVars.primaryNavBackground);
    if (lightness > 50) {
      backgroundColor = "var(--secondaryNavBackground)";
      color = "var(--secondaryNavBackgroundTextColor)";
    }
  }

  const languageButtonContainerClassName = classNames({
    [styles.languageButtonContainer]: true,
    [styles.whiteText]: open,
  });

  const containerClassName = classNames({
    [styles.container]: true,
  });

  return (
    <Dialog.Root open={open} modal={false}>
      <div ref={ref} className={containerClassName}>
        <div className={styles.languageButtonShadow} />
        <button
          data-testid={"language-trigger"}
          className={languageButtonContainerClassName}
          style={{ backgroundColor, color }}
          onClick={toggleOpen}
        >
          <span>
            <div id={"arrows-small-width"}>
              <FontAwesome
                prefix={"fas"}
                name={"fa-angle-down"}
                rotate={open ? 180 : undefined}
              />
            </div>
            <TranslatedText>Language</TranslatedText>
            <div id={"arrows-large-width"}>
              <FontAwesome
                prefix={"fas"}
                name={"fa-angle-down"}
                rotate={open ? 180 : undefined}
              />
            </div>
          </span>
        </button>
        <Dialog.Content
          asChild
          onPointerDownOutside={(e) => {
            if (ref.current && !ref.current.contains(e.target as HTMLElement)) {
              toggleOpen();
            }
          }}
        >
          <LanguageSelector handleSelect={toggleOpen} />
        </Dialog.Content>
      </div>
    </Dialog.Root>
  );
});

export default LanguageTool;
